import axios from 'axios';
import { DHLError, getCurrentDate, axiosRequest, handleHttpResponse, isSuccessfulResponse } from '@general/js/utils';
import BenefitQuotesServices from '../BenefitQuotes/BenefitQuotesServices';
import AbstractClass from './ClaimsAbstractServices';
import { Services } from '../Services';

export default class ClaimsServices extends AbstractClass {
  /**
   * Implicitly returns a Promise for fetching list of members, their products and modalities and payment details.
   *
   * Note that by default `fetchPathway` is to be `"E"`
   */
  static async getMembersProductsAndModalities(memberNumber, fetchPathway = 'E') {
    try {
      const queryParams = {
        params: {
          memberNumber,
          fetchPathway,
          currentDate: getCurrentDate(),
        },
      };
      const response = await axiosRequest().get(Services.MY_CLAIMS.CLAIMS.GET_CLAIMS_ACCOUNT_MEMBERS_PRODUCTS_AND_MODALITIES, queryParams);

      if (handleHttpResponse(response) && response.data.claimsAccount && response.data.claimsMemberModalities) {
        if (response.data.claimsMemberModalities.members && response.data.claimsMemberModalities.members.length > 0) return response.data;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMembersProductsAndModalities() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for fetching multi-step form related reference data from Kentico.
   */
  static async getMultiStepFormReferenceData() {
    try {
      const response = await axios.get(Services.MY_CLAIMS.CLAIMS.GET_REFERENCE_DATA_MULTI_STEP_FORM);

      // Only check for the critical items, e.g. tooth number options since it is required to populate an input field.
      if (handleHttpResponse(response) && response.data.toothNumberOptions && response.data.toothNumberOptions.length > 0) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getMultiStepFormReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Get ref data of claims landing page
   */
  static async getLandingPageReferenceData(memberNumber, currentDate) {
    try {
      const response = await axios.get(Services.MY_CLAIMS.CLAIMS.GET_REFERENCE_DATA_LANDING_PAGE(memberNumber), { params: { currentDate } });

      if (handleHttpResponse(response)) {
        // return member number string token swapped data
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getLandingPageReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for fetching list of providers from OHI.
   *
   * NOTE: This api uses one from BenefitQuoteServices
   */
  static getProvidersForSelectedModality(modalityCode, providerId, lastName, postcode, state) {
    return BenefitQuotesServices.getProvidersForSelectedModality(modalityCode, providerId, lastName, postcode, state);
  }

  /**
   * Implicitly returns a Promise for fetching list of services for a given modality code.
   *
   * NOTE: This api uses one from BenefitQuoteServices
   */
  static getServicesForSelectedModality(modalityCode) {
    return BenefitQuotesServices.getServicesForSelectedModality(modalityCode);
  }

  /**
   * Implicitly returns a Promise for submitting the claim to OHI.
   * @param {string} memberNumber policy number of member submitting claim
   * @param {string} accessingPersonCode member identifier of member that the claim is being created for
   * @param {string} providerId Id of provider
   * @param {string} providerFlexCode flex code of provider
   * @param {string} subProviderIdentifier provider identifier
   * @param {string} subProviderIdentifierType provider identifier type flex code
   * @param {array} serviceList list of services to be submitted
   * @param {string} productCode code of the selected product
   * @returns {object} response from claims submission or error if unsuccessful
   */
  static async submitClaim(
    memberNumber,
    accessingPersonCode,
    providerId,
    providerFlexCode,
    subProviderIdentifier,
    subProviderIdentifierType,
    claimAccountId,
    productCode,
    serviceList,
  ) {
    const payload = {
      providerId,
      providerFlexCode,
      subProviderIdentifier,
      subProviderIdentifierType,
      claimAccountId,
      productCode,
      serviceList,
    };
    try {
      const response = await axiosRequest().post(Services.MY_CLAIMS.CLAIMS.SUBMIT_CLAIM(memberNumber, accessingPersonCode), payload);
      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`submitClaim() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for submitting the snap and send claim
   * @param {*} memberNumber policy number of member submitting claim
   * @param {*} uploadedFileDateTimestamp date timestamp in ISO format
   * @returns response from submission or error if unsuccessful
   */
  static async submitSnapAndSend(memberNumber, uploadedFileDateTimestamp) {
    try {
      const response = await axiosRequest().post(Services.MY_CLAIMS.CLAIMS.SUBMIT_SNAP_AND_SEND(memberNumber), { uploadedFileDateTimestamp });
      // Receive status code 201 when response is successful
      if (isSuccessfulResponse(response)) {
        return response;
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`submitSnapAndSend() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  static async submitFile(memberNumber, accessingPersonCode, category, file, dateTimestamp, serviceId, serviceDate) {
    try {
      const formData = new FormData();
      formData.append('file', file, file.name.replaceAll("'", '_'));

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          'related-person-code': accessingPersonCode,
          'meta-service-code': serviceId,
          'meta-date-of-service': serviceDate,
        },
      };

      const response = await axiosRequest().post(Services.MY_CLAIMS.CLAIMS.FILE_UPLOAD(memberNumber, category, dateTimestamp), formData, config);
      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`submitFile() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for fetching details of a given claim.
   */
  static async getClaimDetails(claimId, memberNumber, accessingPersonCode, productCode) {
    try {
      const response = await axiosRequest().get(Services.MY_CLAIMS.CLAIMS.GET_CLAIM_DETAILS(memberNumber, accessingPersonCode, claimId, productCode));
      return handleHttpResponse(response);
    } catch (error) {
      throw new DHLError(`getClaimDetails() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Implicitly returns a Promise for searching all claims for a policy.
   */
  static async getClaimsSummary(memberNumber) {
    try {
      const response = await axiosRequest().get(Services.MY_CLAIMS.CLAIMS.GET_CLAIMS_SUMMARY(memberNumber));

      if (handleHttpResponse(response) && response.data.claims) {
        return response.data;
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`search() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }
}
